import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const LocationContext = createContext();

// How far a shopper has to move before the nearby list is worth redrawing.
const MOVED_M = 500;
const metres = (a, b) => {
  const R = 6371000, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180, la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const LocationProvider = ({ children }) => {
  const [country, setCountryState] = useState(() => localStorage.getItem("lp-country") || "");
  const [city, setCity] = useState(() => localStorage.getItem("lp-city") || "");
  const [query, setQuery] = useState("");
  const [gps, setGpsState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lp-gps") || "null"); } catch { return null; }
  });
  const [place, setPlaceState] = useState(() => localStorage.getItem("lp-place") || "");
  const [unit, setUnitState] = useState(() => localStorage.getItem("lp-unit") || "km");
  const [tourist, setTouristState] = useState(() => localStorage.getItem("lp-tourist") === "1");
  const [savedBases, setSavedBases] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lp-bases") || "[]"); } catch { return []; }
  });
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState("");
  // "refresh" (default) re-reads the pin on each visit once permission exists; "live" keeps
  // following while the shopper browses; "off" pins them where they are.
  const [followMode, setFollowModeState] = useState(() => localStorage.getItem("lp-follow") || "refresh");
  const gpsRef = useRef(gps);
  gpsRef.current = gps;

  const setFollowMode = (m) => {
    setFollowModeState(m);
    localStorage.setItem("lp-follow", m);
  };

  const [autoCountry, setAutoCountry] = useState(() => localStorage.getItem("lp-auto-country") || null);
  const setCountry = (c) => {
    setCountryState(c);
    setCity("");
    localStorage.setItem("lp-country", c);
    localStorage.setItem("lp-city", "");
    setAutoCountry(null);
    localStorage.removeItem("lp-auto-country");
  };
  const clearAutoCountry = () => { setAutoCountry(null); localStorage.removeItem("lp-auto-country"); };

  // First visit only: guess the visitor's country by IP so the map opens on their own country
  // instead of always Trinidad. Runs once, never overrides a saved choice or an active GPS pin.
  useEffect(() => {
    if (country || gps || localStorage.getItem("lp-geo-detected")) return;
    localStorage.setItem("lp-geo-detected", "1");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo/ip-country`)
      .then((r) => r.json())
      .then((d) => {
        if (d && d.country) {
          setCountry(d.country);
          setAutoCountry(d.country);
          localStorage.setItem("lp-auto-country", d.country);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const chooseCity = (c) => {
    setCity(c);
    localStorage.setItem("lp-city", c);
  };

  const setGps = (g) => {
    setGpsState(g);
    if (g) {
      localStorage.setItem("lp-gps", JSON.stringify(g));
    } else {
      localStorage.removeItem("lp-gps");
      localStorage.removeItem("lp-place");
      setPlaceState("");
    }
  };

  const setPlace = (p) => {
    setPlaceState(p || "");
    if (p) localStorage.setItem("lp-place", p);
    else localStorage.removeItem("lp-place");
  };

  const setUnit = (u) => { setUnitState(u); localStorage.setItem("lp-unit", u); };
  const toggleUnit = () => setUnit(unit === "km" ? "mi" : "km");

  const setTourist = (v) => {
    setTouristState(!!v);
    if (v) localStorage.setItem("lp-tourist", "1");
    else localStorage.removeItem("lp-tourist");
  };

  const persistBases = (arr) => { setSavedBases(arr); localStorage.setItem("lp-bases", JSON.stringify(arr)); };
  const addBase = (b) => {
    if (!b || b.lat == null || b.lng == null) return;
    if (savedBases.some((x) => Math.abs(x.lat - b.lat) < 1e-4 && Math.abs(x.lng - b.lng) < 1e-4)) return;
    const label = ((b.label || (b.place ? b.place.split(",")[0] : "") || "Base")).trim();
    persistBases([...savedBases, { id: Date.now().toString(), label, place: b.place || label, lat: b.lat, lng: b.lng }].slice(-5));
  };
  const removeBase = (id) => persistBases(savedBases.filter((x) => x.id !== id));

  // Gesture-triggered geolocation (graceful — called from a button, not auto-fired on load)
  const requestLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) { setGeoError("unsupported"); resolve(false); return; }
    setLocating(true);
    setGeoError("");
    localStorage.setItem("lp-geo-asked", "1");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const g = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGpsState(g);
        localStorage.setItem("lp-gps", JSON.stringify(g));
        setPlaceState("your location");
        localStorage.setItem("lp-place", "your location");
        setLocating(false);
        resolve(true);
      },
      (err) => {
        setGeoError(err && err.code === 1 ? "denied" : "error");
        setLocating(false);
        resolve(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });

  // Once a shopper has granted permission, their pin refreshes itself — silently, no prompt, no
  // button. Nothing happens for anyone who said no, or who turned following off.
  useEffect(() => {
    if (followMode === "off" || !navigator.geolocation) return undefined;
    let watchId = null;
    let cancelled = false;
    const apply = (pos) => {
      const g = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      const prev = gpsRef.current;
      if (prev && metres(prev, g) < MOVED_M) return;
      gpsRef.current = g;
      setGpsState(g);
      localStorage.setItem("lp-gps", JSON.stringify(g));
      if (!localStorage.getItem("lp-place")) {
        setPlaceState("your location");
        localStorage.setItem("lp-place", "your location");
      }
    };
    const start = () => {
      if (cancelled) return;
      navigator.geolocation.getCurrentPosition(apply, () => {}, { timeout: 8000, maximumAge: 60000 });
      if (followMode === "live") {
        watchId = navigator.geolocation.watchPosition(apply, () => {},
          { timeout: 15000, maximumAge: 30000 });
      }
    };
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" })
        .then((s) => { if (s.state === "granted") start(); })
        .catch(() => {});
    } else if (localStorage.getItem("lp-gps")) {
      start();  // no Permissions API (Safari): a stored pin means they already said yes
    }
    return () => { cancelled = true; if (watchId != null) navigator.geolocation.clearWatch(watchId); };
  }, [followMode]);

  return (
    <LocationContext.Provider value={{ country, city, query, gps, place, unit, tourist, savedBases, locating, geoError, followMode, autoCountry, clearAutoCountry, setCountry, setCity: chooseCity, setQuery, setGps, setPlace, setUnit, toggleUnit, setTourist, addBase, removeBase, requestLocation, setFollowMode }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationFilter = () => useContext(LocationContext);
